import { DataSource } from '@angular/cdk/table';
import { Pagination, TradeRestriction } from '../../models';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { RestrictionService } from '../../../core/data-services/restriction.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';
import { PaginatedList } from '../../../xform-compat';

export class TradeRestrictionDataSource extends DataSource<TradeRestriction> {
  private readonly data$ = new BehaviorSubject<Array<TradeRestriction>>([]);
  private readonly loading$ = new BehaviorSubject<boolean>(false);
  private readonly total$ = new BehaviorSubject<number>(0);
  private readonly filter$ = new BehaviorSubject<any>({});
  private readonly pagination$: BehaviorSubject<Pagination>;
  private readonly refresh$ = new Subject<void>();

  public readonly isLoading$ = this.loading$.asObservable();
  public readonly totalCount$ = this.total$.asObservable();
  // The current page's records, for layouts drawn outside the mat-table (the company tab's cards)
  public readonly records$ = this.data$.asObservable();

  constructor(
    private restrictionService: RestrictionService,
    private sort: MatSort,
    pagination: Pagination
  ) {
    super();
    this.pagination$ = new BehaviorSubject<Pagination>(pagination)
  }

  connect(): Observable<Array<TradeRestriction>> {
    const triggers$ = merge(
      this.sort.sortChange,
      this.pagination$,
      this.filter$,
      this.refresh$
    );

    return triggers$.pipe(
      tap(() => this.loading$.next(true)),
      switchMap(() =>
        this.restrictionService.getRestrictions({
          ...this.filter$.value,
          page: this.pagination$.value.page - 1,
          pagesize: this.pagination$.value.pagesize,
          sort: this.sort.active,
          direction: this.sort.direction
        })
      ),
      tap((res: PaginatedList<TradeRestriction>) => {
        this.total$.next(res.totalRecords);
      }),
      map((res: PaginatedList<TradeRestriction>) => res.records.map(r => new TradeRestriction(r))),
      tap((records: Array<TradeRestriction>) => {
        this.data$.next(records);
        this.loading$.next(false);
      })
    );
  }

  disconnect(): void {
    this.data$.complete();
    this.loading$.complete();
    this.total$.complete();
    this.filter$.complete();
    this.refresh$.complete();
  }

  public setFilter(value: any) {
    this.filter$.next(value);
  }

  public setPagination(value: Pagination) {
    this.pagination$.next(value);
  }

  public refresh() {
    this.refresh$.next();
  }

}
